import React from 'react';
import Icon from '../../../components/AppIcon';

const AlertPanel = ({ alerts, loading }) => {
  const getPriorityStyles = (priority) => {
    switch (priority) {
      case 'critical':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          icon: 'text-red-600',
          text: 'text-red-800',
          badge: 'bg-red-100 text-red-700'
        };
      case 'warning':
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          icon: 'text-yellow-600',
          text: 'text-yellow-800',
          badge: 'bg-yellow-100 text-yellow-700'
        };
      case 'info':
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          icon: 'text-blue-600',
          text: 'text-blue-800',
          badge: 'bg-blue-100 text-blue-700'
        };
      case 'success':
      default:
        return {
          bg: 'bg-green-50',
          border: 'border-green-200',
          icon: 'text-green-600',
          text: 'text-green-800',
          badge: 'bg-green-100 text-green-700'
        };
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'revenue':
        return 'DollarSign';
      case 'overdue_invoices':
        return 'FileText';
      case 'technician_workload':
        return 'Users';
      case 'customer_feedback':
        return 'MessageSquare';
      default:
        return 'AlertCircle';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'critical':
        return 'Crítico';
      case 'warning':
        return 'Advertencia';
      case 'info':
        return 'Información';
      case 'success':
        return 'Normal';
      default:
        return priority;
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg shadow-elevation-1 p-6">
        <div className="flex items-center gap-3 mb-4">
          <Icon name="Bell" className="w-5 h-5 text-foreground" />
          <h2 className="text-xl font-heading font-semibold text-foreground">
            Alertas del Sistema
          </h2>
        </div>
        <div className="flex justify-center py-8">
          <Icon name="Loader2" className="w-6 h-6 text-blue-600 animate-spin" />
        </div>
      </div>
    );
  }

  if (!alerts || alerts?.length === 0) {
    return (
      <div className="bg-card rounded-lg shadow-elevation-1 p-6">
        <div className="flex items-center gap-3 mb-4">
          <Icon name="Bell" className="w-5 h-5 text-foreground" />
          <h2 className="text-xl font-heading font-semibold text-foreground">
            Alertas del Sistema
          </h2>
        </div>
        <div className="text-center py-8 text-muted-foreground">
          <Icon name="CheckCircle" className="w-12 h-12 mx-auto mb-2 text-green-500" />
          <p>No hay alertas activas</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-elevation-1 p-6">
      <div className="flex items-center gap-3 mb-4">
        <Icon name="Bell" className="w-5 h-5 text-foreground" />
        <h2 className="text-xl font-heading font-semibold text-foreground">
          Alertas del Sistema
        </h2>
        <span className="ml-auto text-sm font-medium text-muted-foreground">
          {alerts?.length} alerta(s)
        </span>
      </div>

      <div className="space-y-3">
        {alerts?.map((alert, index) => {
          const styles = getPriorityStyles(alert?.priority);
          const iconName = getAlertIcon(alert?.type);

          return (
            <div
              key={index}
              className={`${styles?.bg} ${styles?.border} border rounded-lg p-4 transition-all hover:shadow-md`}
            >
              <div className="flex items-start gap-3">
                <div className={`flex-shrink-0 ${styles?.icon}`}>
                  <Icon name={iconName} className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded ${styles?.badge}`}>
                      {getPriorityLabel(alert?.priority)}
                    </span>
                  </div>
                  <p className={`text-sm font-medium ${styles?.text}`}>
                    {alert?.message}
                  </p>
                  
                  {/* Additional details based on alert type */}
                  {alert?.type === 'revenue' && alert?.percentage !== undefined && (
                    <div className="mt-2">
                      <div className="w-full bg-white rounded-full h-2 overflow-hidden">
                        <div
                          className={`h-full transition-all ${
                            alert?.priority === 'critical' ? 'bg-red-500' :
                            alert?.priority === 'warning'? 'bg-yellow-500' : 'bg-green-500'
                          }`}
                          style={{ width: `${Math.min(alert?.percentage, 100)}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {alert?.type === 'overdue_invoices' && alert?.count > 0 && (
                    <div className="mt-2 text-xs text-muted-foreground">
                      {alert?.invoices?.slice(0, 3)?.map((invoice, idx) => (
                        <div key={idx} className="truncate">
                          • {invoice?.clientName} - ${invoice?.total}
                        </div>
                      ))}
                      {alert?.invoices?.length > 3 && (
                        <div className="text-xs font-medium mt-1">
                          +{alert?.invoices?.length - 3} más
                        </div>
                      )}
                    </div>
                  )}

                  {alert?.type === 'technician_workload' && alert?.technicians?.length > 0 && (
                    <div className="mt-2 text-xs text-muted-foreground">
                      {alert?.technicians?.slice(0, 3)?.map((tech, idx) => (
                        <div key={idx} className="truncate">
                          • {tech?.user?.fullName || 'Técnico'} - {tech?.currentWorkload} órdenes
                        </div>
                      ))}
                    </div>
                  )}

                  {alert?.type === 'customer_feedback' && alert?.recentFeedback?.length > 0 && (
                    <div className="mt-2 text-xs text-muted-foreground">
                      {alert?.recentFeedback?.map((feedback, idx) => (
                        <div key={idx} className="truncate">
                          • {feedback?.workOrder?.folioOt || 'N/A'} - {feedback?.workOrder?.client?.nombre || 'Cliente'} ({feedback?.satisfactionRating}⭐)
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AlertPanel;